import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AppEmptyState, AppSelect, AppSpinner } from '../ui';
import KslFrame from './KslFrame';
import { notify } from '../common/AppToast';
import klubService from '../../services/klubService';
import { ROUTES } from '../../config/routes';
import { CE_CHECK_COLUMNS, ceCheckPatch, ceStepChecked } from '../../utils/klubUtils';
import useAppConfirm from '../../hooks/useAppConfirm';
import '../../styles/klub-dashboard.scss';

const GRID = 'minmax(180px, 1.4fr) 110px minmax(120px, 0.8fr) repeat(4, 72px) 168px';

const KlubCeWorkList = ({ items, commerciaux = [], loading, onChanged }) => {
  const { ask, modal } = useAppConfirm();
  const [pendingKeys, setPendingKeys] = useState(() => new Set());
  const [edit, setEdit] = useState(null);
  const skipBlur = useRef(false);

  const toggleCheck = async (ce, code) => {
    const key = `${ce.id}:${code}`;
    if (pendingKeys.has(key)) return;
    const checked = !ceStepChecked(ce, code);
    const patch = ceCheckPatch(ce, code, checked);
    setPendingKeys((prev) => new Set(prev).add(key));
    try {
      await klubService.updateCe(ce.id, patch);
      await onChanged?.();
    } catch (err) {
      notify.error(err.message || 'Impossible de mettre à jour la fiche CE');
    } finally {
      setPendingKeys((prev) => {
        const copy = new Set(prev);
        copy.delete(key);
        return copy;
      });
    }
  };

  const beginEdit = (ce, field) => {
    const value = field === 'entreprise'
      ? (ce.entreprise ?? '')
      : field === 'commercial'
        ? (ce.commercial?.id != null ? String(ce.commercial.id) : '')
        : (ce.nombre_salaries ?? '');
    setEdit({ id: ce.id, field, value });
  };

  const cancelEdit = () => setEdit(null);

  const saveEdit = async (ce, field, rawValue) => {
    if (!edit || edit.id !== ce.id || edit.field !== field) return;
    const raw = rawValue != null ? rawValue : edit.value;
    setEdit(null);

    let patch = null;
    if (field === 'entreprise') {
      const next = String(raw).trim();
      if (!next || next === (ce.entreprise ?? '')) return;
      patch = { entreprise: next };
    } else if (field === 'commercial') {
      const next = raw === '' ? null : Number(raw);
      if ((ce.commercial?.id ?? null) === next) return;
      patch = { commercial_id: next };
    } else {
      const text = String(raw).trim();
      const next = text === '' ? null : Number(text);
      if (next != null && Number.isNaN(next)) return;
      if ((ce.nombre_salaries ?? null) === next) return;
      patch = { nombre_salaries: next };
    }

    const key = `${ce.id}:${field}`;
    setPendingKeys((prev) => new Set(prev).add(key));
    try {
      await klubService.updateCe(ce.id, patch);
      await onChanged?.();
    } catch (err) {
      notify.error(err.message || 'Impossible d’enregistrer la modification');
    } finally {
      setPendingKeys((prev) => {
        const copy = new Set(prev);
        copy.delete(key);
        return copy;
      });
    }
  };

  const removeCe = async (ce) => {
    const confirmed = await ask({
      title: 'Retirer de la liste CE',
      message: `Retirer ${ce.entreprise} de la liste CE ?\nLa fiche site Klub est conservée. Les salariés enregistrés sur cette fiche CE seront supprimés.`,
      confirmLabel: 'Supprimer',
      confirmVariant: 'danger',
      confirmIcon: 'mdi:delete-outline',
    });
    if (!confirmed) return;
    const key = `${ce.id}:delete`;
    setPendingKeys((prev) => new Set(prev).add(key));
    try {
      await klubService.deleteCe(ce.id);
      notify.success(`${ce.entreprise} a été retiré de la liste CE`);
      await onChanged?.();
    } catch (err) {
      notify.error(err.message || 'Impossible de supprimer la fiche CE');
    } finally {
      setPendingKeys((prev) => {
        const copy = new Set(prev);
        copy.delete(key);
        return copy;
      });
    }
  };

  if (loading) {
    return <AppSpinner />;
  }
  if (items.length === 0) {
    return <AppEmptyState icon="mdi:domain" message="Aucune entreprise" />;
  }

  return (
    <>
      <KslFrame
        columns={GRID}
        minWidth="980px"
        header={(
          <div className="ksl__row ksl__head">
            <div>Entreprise</div>
            <div>Nb salariés</div>
            <div>Commercial</div>
            {CE_CHECK_COLUMNS.map((column) => (
              <div key={column.code} className="ksl__head-step" title={column.title}>
                <span>{column.label}</span>
              </div>
            ))}
            <div />
          </div>
        )}
      >
        {items.map((ce) => {
          const checks = Object.fromEntries(
            CE_CHECK_COLUMNS.map((column) => [column.code, ceStepChecked(ce, column.code)]),
          );
          const editing = edit?.id === ce.id ? edit.field : null;
          return (
            <div key={ce.id} className="ksl__row ksl__client">
              <div className="ksl__name">
                {editing === 'entreprise' ? (
                  <input
                    className="ksl__inline ksl__inline--grow"
                    value={edit.value}
                    autoFocus
                    aria-label={`Entreprise ${ce.entreprise}`}
                    onChange={(event) => setEdit({ ...edit, value: event.target.value })}
                    onBlur={() => {
                      if (skipBlur.current) {
                        skipBlur.current = false;
                        return;
                      }
                      saveEdit(ce, 'entreprise');
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                      if (event.key === 'Escape') {
                        skipBlur.current = true;
                        cancelEdit();
                        event.currentTarget.blur();
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="ksl__inline-btn"
                    title="Modifier l’entreprise"
                    disabled={pendingKeys.has(`${ce.id}:entreprise`)}
                    onClick={() => beginEdit(ce, 'entreprise')}
                  >
                    <b>{ce.entreprise}</b>
                  </button>
                )}
              </div>
              <div>
                {editing === 'salaries' ? (
                  <input
                    className="ksl__inline"
                    type="number"
                    min="0"
                    value={edit.value}
                    autoFocus
                    aria-label={`Nombre de salariés de ${ce.entreprise}`}
                    onChange={(event) => setEdit({ ...edit, value: event.target.value })}
                    onBlur={() => {
                      if (skipBlur.current) {
                        skipBlur.current = false;
                        return;
                      }
                      saveEdit(ce, 'salaries');
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') event.currentTarget.blur();
                      if (event.key === 'Escape') {
                        skipBlur.current = true;
                        cancelEdit();
                        event.currentTarget.blur();
                      }
                    }}
                  />
                ) : (
                  <button
                    type="button"
                    className="ksl__inline-btn"
                    title="Modifier le nombre de salariés"
                    disabled={pendingKeys.has(`${ce.id}:salaries`)}
                    onClick={() => beginEdit(ce, 'salaries')}
                  >
                    {ce.nombre_salaries ?? '—'}
                  </button>
                )}
              </div>
              <div className="ksl__commercial" title={ce.commercial?.nom || undefined}>
                {editing === 'commercial' ? (
                  <AppSelect
                    size="sm"
                    value={edit.value}
                    aria-label={`Commercial de ${ce.entreprise}`}
                    autoFocus
                    onChange={(event) => {
                      const next = event.target.value;
                      setEdit({ ...edit, value: next });
                      saveEdit(ce, 'commercial', next);
                    }}
                    onBlur={() => {
                      if (edit?.id === ce.id && edit.field === 'commercial') cancelEdit();
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') cancelEdit();
                    }}
                  >
                    <option value="">—</option>
                    {commerciaux.map((commercial) => (
                      <option key={commercial.id} value={String(commercial.id)}>{commercial.nom}</option>
                    ))}
                  </AppSelect>
                ) : (
                  <button
                    type="button"
                    className="ksl__inline-btn"
                    title="Modifier le commercial"
                    disabled={pendingKeys.has(`${ce.id}:commercial`)}
                    onClick={() => beginEdit(ce, 'commercial')}
                  >
                    {ce.commercial?.nom || '—'}
                  </button>
                )}
              </div>
              {CE_CHECK_COLUMNS.map((column) => {
                const done = checks[column.code];
                const key = `${ce.id}:${column.code}`;
                return (
                  <div key={column.code} className="ksl__cell">
                    <button
                      type="button"
                      className={`ksl__sq ${done ? 'is-ok' : 'is-ko'}`}
                      title={`${column.title} · ${done ? 'OK' : 'KO'}`}
                      aria-pressed={done}
                      aria-label={`${column.title} · ${done ? 'OK' : 'KO'}`}
                      disabled={pendingKeys.has(key)}
                      onClick={() => toggleCheck(ce, column.code)}
                    >
                      {done ? '✓' : ''}
                    </button>
                  </div>
                );
              })}
              <div className="ksl__actions">
                {ce.adherent_id ? (
                  <Link className="ksl__btn" to={ROUTES.klub.adherentDetail(ce.adherent_id)}>Voir</Link>
                ) : (
                  <span className="ksl__btn ksl__btn--sec" title="Aucune fiche adhérent">Voir</span>
                )}
                <button
                  type="button"
                  className="ksl__btn ksl__btn--danger"
                  disabled={pendingKeys.has(`${ce.id}:delete`)}
                  onClick={() => removeCe(ce)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          );
        })}
      </KslFrame>
      {modal}
    </>
  );
};

export default KlubCeWorkList;
